import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CirclePlus, Clapperboard, Film, ImagePlus, Palette } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface CanvasLeftBarProps {
    onAddProducts: () => void;
    /** Upload an image, or import one from a link, as a source node. */
    onAddImage?: () => void;
    /** Open the video timeline editor (from the selected images, or empty). */
    onOpenVideoEditor?: () => void;
    /** Brief → storyboard → shots → clips → timeline. */
    onOpenVideoStory?: () => void;
    onOpenBrandKit: () => void;
}

interface BarButtonProps {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
}

const BarButton: React.FC<BarButtonProps> = ({ icon, label, onClick }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                variant="outline"
                size="icon"
                onClick={onClick}
                aria-label={label}
                className="size-10 text-muted-foreground hover:text-foreground [&_svg]:size-5"
            >
                {icon}
            </Button>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
);

/**
 * Vertical icon bar pinned to the left edge of the canvas.
 * Provides:
 *   + Add products (opens product picker panel)
 *   Brand kit (navigates)
 *   Back (navigates to projects list)
 */
export const CanvasLeftBar: React.FC<CanvasLeftBarProps> = ({
    onAddProducts,
    onAddImage,
    onOpenVideoEditor,
    onOpenVideoStory,
    onOpenBrandKit,
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    return (
        <div className="absolute start-4 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-1.5 rounded-lg border bg-background/95 p-1.5 backdrop-blur-sm">
            <BarButton
                icon={<CirclePlus strokeWidth={1.5} />}
                label={t('CONTENT_STUDIO.ADD_PRODUCTS', 'Add products')}
                onClick={onAddProducts}
            />
            {onAddImage && (
                <BarButton
                    icon={<ImagePlus strokeWidth={1.5} />}
                    label={t('CONTENT_STUDIO.ADD_IMAGE.TITLE', 'Add an image')}
                    onClick={onAddImage}
                />
            )}
            {onOpenVideoEditor && (
                <BarButton
                    icon={<Clapperboard strokeWidth={1.5} />}
                    label={t('VIDEO_EDITOR.LEFT_BAR')}
                    onClick={onOpenVideoEditor}
                />
            )}
            {onOpenVideoStory && (
                <BarButton
                    icon={<Film strokeWidth={1.5} />}
                    label={t('VIDEO_EDITOR.STORY.LEFT_BAR')}
                    onClick={onOpenVideoStory}
                />
            )}

            <Separator />

            <BarButton
                icon={<Palette strokeWidth={1.5} />}
                label={t('CONTENT_STUDIO.TOOL_BRAND_KIT', 'Brand kit')}
                onClick={onOpenBrandKit}
            />

            <Separator />

            <BarButton
                icon={<ArrowLeft strokeWidth={1.5} />}
                label={t('CONTENT_STUDIO.TOOL_BACK', 'Back to projects')}
                onClick={() => navigate('/marketing/content-studio')}
            />
        </div>
    );
};
